import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { BehaviorSubject, Observable } from 'rxjs';
import { CompanyProductPhaseBoard } from 'src/app/models/company_product_phase_board.model';

declare global {
  interface Window {
    require: any;
    process: any; // Añadir process en la interfaz de Window
  }
}

function parseFilenameFromContentDisposition(content_disposition: string | null, fallback: string): string {
  if (content_disposition == null || content_disposition.trim() === '') {
    return fallback;
  }

  const filename_star_match = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/i.exec(content_disposition);
  if (filename_star_match?.[1]) {
    try {
      return decodeURIComponent(filename_star_match[1].trim().replace(/^["']|["']$/g, ''));
    } catch {
      // Fall through to ASCII filename=
    }
  }

  const filename_match = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(content_disposition);
  const ascii_filename = (filename_match?.[1] ?? filename_match?.[2] ?? '').trim();

  return ascii_filename !== '' ? ascii_filename : fallback;
}

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  private downloadsCounter: number = 0;
  private loadingSubject: BehaviorSubject<boolean> = new BehaviorSubject(false as boolean);
  public loadingObservable: Observable<boolean> = this.loadingSubject.asObservable();

  constructor() {}

  public enableLoading() {
    if (this.downloadsCounter < 0) this.downloadsCounter = 0;
    this.downloadsCounter++;
    this.refreshLoading();
  }

  public disableLoading() {
    this.downloadsCounter--;
    if (this.downloadsCounter < 0) this.downloadsCounter = 0;
    this.refreshLoading();
  }

  public cloneObj(src: any) {
    const target: any = {};
    for (const key in src) {
      if (src[key] == null) {
        target[key] = null;
      } else if (Array.isArray(src[key])) {
        target[key] = [];
        for (const k2 in src[key]) {
          target[key][k2] = this.cloneObj(src[key][k2]);
        }
      } else if (src[key] instanceof Date) {
        target[key] = new Date(src[key]);
      } else if (typeof src[key] === 'object') {
        target[key] = this.cloneObj(src[key]);
      } else {
        target[key] = src[key];
      }
    }
    return target;
  }

  public diffDays(d1: Date, d2: Date) {
    return d2.getDate() - d1.getDate();
  }

  getStatusTooltip(status: string) {
    let res = 'No reconocido';
    switch (status) {
      case 'active':
        res = 'Activo';
        break;
      case 'inactive':
        res = 'Inactivo, lo ha marcado el cliente';
        break;
      case 'cancelled':
        res = 'Cancelado, se eliminará pronto';
        break;
      case 'expired':
        res = 'Expirado, el cliente no lo ha renovado';
        break;
      case 'deleted':
        res = 'Eliminado de Hetzner';
        break;
    }
    return res;
  }

  downloadPdfFromBlobResponse(response: HttpResponse<any>) {
    const filename = parseFilenameFromContentDisposition(response.headers.get('Content-Disposition'), 'Archivo.pdf');
    const blob = new Blob([response.body], { type: 'application/pdf' });
    saveAs(blob, filename);
  }

  downloadXlsxFromBlobResponse(response: HttpResponse<any>) {
    const filename = parseFilenameFromContentDisposition(response.headers.get('Content-Disposition'), 'Archivo.xlsx');
    const blob = new Blob([response.body], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, filename);
  }

  downloadTextFile(body: string, filename: string) {
    const blob = new Blob([body]);
    saveAs(blob, filename);
  }

  sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  getChartsColorScheme() {
    return {
      domain: ['#0C83E1', '#6BC434', '#CE2525', '#E3B163', '#888888', '#E8F5FF'],
    };
  }

  getGreenRedColorScheme() {
    return {
      domain: ['#6BC434', '#CE2525'],
    };
  }

  getDefaultInitialDate() {
    const today = new Date();
    const first = today.getDate() - today.getDay() + 1;

    const monday = new Date(today.setDate(first));
    return monday;
  }

  getDefaultEndDate() {
    const today = new Date();
    const first = today.getDate() - today.getDay() + 1;
    const last = first + 6;

    const sunday = new Date(today.setDate(last));

    return sunday;
  }

  public dateToString(d: Date): string {
    return this.pad(d.getDate()) + '-' + this.pad(d.getMonth() + 1) + '-' + d.getFullYear();
  }

  public dateToStringYYYYMMDD(d: Date): string {
    return d.getFullYear() + '-' + this.pad(d.getMonth() + 1) + '-' + this.pad(d.getDate());
  }

  /** Kept public for structural compatibility with @bisual/ngx-index-template UtilsService. */
  public pad(value: number): string {
    return value.toString().padStart(2, '0');
  }

  public stringToDate(str: string): Date {
    const str_splitted = str.split('-');
    return new Date(+str_splitted[2], +str_splitted[1] - 1, +str_splitted[0]);
  }

  private refreshLoading() {
    this.loadingSubject.next(this.downloadsCounter > 0);
  }

  downloadCsvFromBlobResponse(response: HttpResponse<any>) {
    const filename = parseFilenameFromContentDisposition(response.headers.get('Content-Disposition'), 'Archivo.csv');
    const blob = new Blob([response.body], { type: 'text/csv' });
    saveAs(blob, filename);
  }

  readFileAsText(file: File, encoding: string) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsText(file, encoding);
    });
  }

  getHeaderArrayFromCsv(csvRecordsArr: any, separator: string) {
    const headers = csvRecordsArr[0].split(separator);
    const headerArray = [];
    for (let j = 0; j < headers.length; j++) {
      headerArray.push(headers[j]);
    }
    return headerArray;
  }

  generateRandomColor() {
    // Genera un número aleatorio entre 0 y 16777215 (0xFFFFFF en decimal)
    let numeroRandom = Math.floor(Math.random() * 0xffffff);
    // Si el color es muy oscuro, lo convertimos a un color más claro
    if (numeroRandom < 0x000000) {
      numeroRandom = 0x000000 + Math.floor(Math.random() * 0xffffff);
    }
    // Convierte el número a una cadena hexadecimal y asegúrate de que tenga siempre 6 caracteres
    const colorHex = `#${numeroRandom.toString(16).padStart(6, '0')}`;
    return colorHex;
  }

  public generateColor(value: number, minValue: number, maxValue: number, maxColor: string) {
    // Normalize the value to a range between 0 and 1
    const normalizedValue = (value - minValue) / (maxValue - minValue);

    // Calculate the alpha (transparency) value based on the normalized value (0 to 1)
    const alpha = Math.round(255 * normalizedValue)
      .toString(16)
      .padStart(2, '0');

    // Calculate the alpha value for the minimum color (using 25 as the minimum alpha)
    const minAlpha = Math.round(25 + normalizedValue * (255 - 25))
      .toString(16)
      .padStart(2, '0');

    // Append the alpha value to the maxColor or minAlpha to get the new color
    const newColor = normalizedValue === 1 ? maxColor + alpha : maxColor + minAlpha;

    return newColor;
  }

  public emailRegex(): RegExp {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  }

  public urlRegex(): RegExp {
    return /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/;
  }

  public isValidEmail(string: string) {
    return this.emailRegex().test(string);
  }

  getWeekNumberOfMonth(date: Date | string): number {
    if (typeof date === 'string') date = new Date(date);

    const targetDayOfWeek = date.getDay(); // Día de la semana de la fecha (0=domingo, 1=lunes, ..., 6=sábado)
    const dayOfMonth = date.getDate(); // Día del mes
    let occurrenceCount = 0;

    // Recorremos desde el día 1 hasta la fecha proporcionada
    for (let day = 1; day <= dayOfMonth; day++) {
      const currentDate = new Date(date.getFullYear(), date.getMonth(), day);
      if (currentDate.getDay() === targetDayOfWeek) {
        occurrenceCount++; // Contamos los días que coinciden con el día objetivo
      }
    }

    return occurrenceCount; // Retornamos cuántas veces ha ocurrido ese día de la semana
  }

  arraysAreEqual<T>(arr1: T[], arr2: T[]): boolean {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((value, index) => value === arr2[index]);
  }

  dateToDayjs(d: Date) {
    return dayjs(new Date(d).toISOString().split('T')[0], 'YYYY-MM-DD');
  }

  formatCompletedAgo(value: string | Date | null | undefined): string {
    if (!value) {
      return '—';
    }

    const completed_at = dayjs(value).startOf('day');
    const today = dayjs().startOf('day');

    if (!completed_at.isValid()) {
      return '—';
    }

    if (completed_at.isSame(today) || completed_at.isAfter(today)) {
      return 'hoy';
    }

    const total_months = today.diff(completed_at, 'month');

    if (total_months < 1) {
      const days = today.diff(completed_at, 'day');

      if (days === 1) {
        return 'hace 1 día';
      }

      return `hace ${days} días`;
    }

    const years = Math.floor(total_months / 12);
    const months = total_months % 12;
    const parts: string[] = [];

    if (years === 1) {
      parts.push('1 año');
    } else if (years > 1) {
      parts.push(`${years} años`);
    }

    if (months === 1) {
      parts.push('1 mes');
    } else if (months > 1) {
      parts.push(`${months} meses`);
    }

    return `hace ${parts.join(' y ')}`;
  }

  normalizeMadridDateForApi(value: Date | string, boundary: 'start' | 'end'): string {
    const madridDateTime = dayjs(value).tz('Europe/Madrid');
    const withBoundary = boundary === 'start' ? madridDateTime.startOf('day') : madridDateTime.endOf('day');

    return withBoundary.utc().toISOString();
  }

  longestWordUnicode(text: string): string | null {
    const words = text.match(/\b[\p{L}0-9_]+\b/gu);
    if (!words) return null;
    return words.reduce((a, b) => (b.length > a.length ? b : a));
  }

  generateSlug(text: string): string {
    return text
      .trim()
      .toLowerCase()
      .replace(/[^\w ]+/g, '')
      .replace(/ +/g, '-');
  }

  async parseApiErrorFromBlob(error: unknown): Promise<string | undefined> {
    const http_error = error as { error?: Blob };

    if (!(http_error?.error instanceof Blob)) {
      return undefined;
    }

    try {
      const error_text = await http_error.error.text();
      const parsed_error = JSON.parse(error_text) as { error?: string };

      return parsed_error.error;
    } catch {
      return undefined;
    }
  }

  downloadDocumentFromBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  static isCompanyProductPhaseBoardPast(board: CompanyProductPhaseBoard | null | undefined): boolean {
    if (board?.end_date == null) {
      return false;
    }

    return new Date(board.end_date) < new Date();
  }

  isCompanyProductPhaseBoardPast(board: CompanyProductPhaseBoard | null | undefined): boolean {
    return UtilsService.isCompanyProductPhaseBoardPast(board);
  }

  static isCompanyProductPhaseBoardFuture(board: CompanyProductPhaseBoard | null | undefined): boolean {
    if (board?.start_date == null) {
      return false;
    }

    return new Date(board.start_date) > new Date();
  }

  isCompanyProductPhaseBoardFuture(board: CompanyProductPhaseBoard | null | undefined): boolean {
    return UtilsService.isCompanyProductPhaseBoardFuture(board);
  }

  static columnMoveWouldCompleteBoardBudgets(origin_slug: string | null, dest_slug: string | null): boolean {
    if (!origin_slug || !dest_slug) {
      return false;
    }

    if (origin_slug === 'under-review' && dest_slug === 'todo') {
      return true;
    }

    return ['under-review', 'todo', 'in-progress'].includes(origin_slug) && ['test', 'done'].includes(dest_slug);
  }

  extractPlainTextFromHtml(html_content: string): string {
    const parsed_document = new DOMParser().parseFromString(html_content, 'text/html');

    return parsed_document.body.textContent ?? '';
  }

  async copyTextToClipboard(text: string): Promise<void> {
    if (window.electron?.copyToClipboard) {
      window.electron.copyToClipboard(text);
      return;
    }

    await navigator.clipboard.writeText(text);
  }

  async copyImageToClipboard(png_blob: Blob): Promise<void> {
    if (window.electron?.copyImageToClipboard) {
      const bytes = new Uint8Array(await png_blob.arrayBuffer());
      window.electron.copyImageToClipboard(bytes);
      return;
    }

    await navigator.clipboard.write([new ClipboardItem({ 'image/png': png_blob })]);
  }

  buildHtmlContentExcerpt(html_content: string | null, max_length = 120): string {
    if (html_content == null || html_content.trim() === '') {
      return '';
    }

    const text_content = this.extractPlainTextFromHtml(html_content).replace(/\s+/g, ' ').trim();

    if (text_content.length <= max_length) {
      return text_content;
    }

    return `${text_content.slice(0, max_length).trimEnd()}…`;
  }

  formatDistanceMeters(distance_in_meters: number | null): string {
    if (distance_in_meters === null) {
      return 'Distancia desconocida';
    }

    if (distance_in_meters < 1000) {
      return `${Math.round(distance_in_meters)} m`;
    }

    return `${(distance_in_meters / 1000).toFixed(1)} km`;
  }

  joinUserNames(users: { full_name: string }[]): string {
    return users.map(user => user.full_name).join(', ');
  }
}
